/**
 * Product Transformer
 * Handles transformation of product data from API to application domain model
 * Single Responsibility: Product data transformation only
 */

/**
 * Parses description to extract alert information if present
 * Handles cases where description contains: { "type": "alert", "message": "..." } rest of text
 * Example: '{ "type": "alert", "message": "You will receive this product on Wednesday November 25th" } This is something else about the product'
 * @param {string} description - Raw description string
 * @returns {Object} Object with alertMessage, description, and requiresConfirmation flag
 */
function parseDescriptionWithAlert(description) {
  if (!description || typeof description !== "string") {
    return {
      description: description || "",
      alertMessage: null,
      requiresConfirmation: false
    };
  }

  // Try to find and parse JSON object at the start of the string
  // Look for pattern: { ... } followed by text
  const trimmed = description.trim();

  // Find the first complete JSON object
  let braceCount = 0;
  let jsonEndIndex = -1;
  let inString = false;
  let escapeNext = false;

  for (let i = 0; i < trimmed.length; i++) {
    const char = trimmed[i];

    if (escapeNext) {
      escapeNext = false;
      continue;
    }

    if (char === "\\") {
      escapeNext = true;
      continue;
    }

    if (char === '"') {
      inString = !inString;
      continue;
    }

    if (!inString) {
      if (char === "{") {
        if (braceCount === 0 && i > 0) {
          // JSON object doesn't start at the beginning
          break;
        }
        braceCount++;
      } else if (char === "}") {
        braceCount--;
        if (braceCount === 0) {
          jsonEndIndex = i;
          break;
        }
      }
    }
  }

  // If we found a complete JSON object, try to parse it
  if (jsonEndIndex > 0) {
    try {
      const jsonString = trimmed.substring(0, jsonEndIndex + 1);
      const parsed = JSON.parse(jsonString);

      if (
        parsed &&
        typeof parsed === "object" &&
        parsed.type === "alert" &&
        parsed.message
      ) {
        const restOfText = trimmed.substring(jsonEndIndex + 1).trim();
        return {
          description: restOfText || "",
          alertMessage: parsed.message,
          requiresConfirmation: true
        };
      }
    } catch (e) {
      // JSON parsing failed, continue with normal description
    }
  }

  // Also try to parse if entire description is a JSON object
  try {
    const parsed = JSON.parse(description);
    if (
      parsed &&
      typeof parsed === "object" &&
      parsed.type === "alert" &&
      parsed.message
    ) {
      return {
        description: "",
        alertMessage: parsed.message,
        requiresConfirmation: true
      };
    }
  } catch (e) {
    // Not a JSON object, continue with normal description
  }

  // No alert found, return description as-is
  return {
    description: description,
    alertMessage: null,
    requiresConfirmation: false
  };
}

/**
 * Transforms a single product from API response to application model
 * @param {Object} rawProduct - Raw product object from API
 * @returns {Object|null} Transformed product object or null if invalid
 */
export function transformProduct(rawProduct) {
  if (!rawProduct || typeof rawProduct !== "object") {
    return null;
  }

  // Parse description to extract alert information
  const descriptionData = parseDescriptionWithAlert(rawProduct.description);

  return {
    ...rawProduct,
    // Ensure categories is always an array
    categories: Array.isArray(rawProduct.categories)
      ? rawProduct.categories
      : rawProduct.category
      ? [rawProduct.category]
      : [],
    // Normalize other fields as needed
    name: rawProduct.name || "",
    price: rawProduct.price || 0,
    description: descriptionData.description,
    alertMessage: descriptionData.alertMessage,
    requiresConfirmation: descriptionData.requiresConfirmation,
    stock: rawProduct.stock || 0,
    dontAllowSaleIfNoStock: !!rawProduct.dont_allow_sale_if_no_stock,
    manualIs86d: !!rawProduct.manual_is_86d,
    autoIs86d: !!rawProduct.auto_is_86d,
    is86d: !!rawProduct.is_86d,
    is_86d: !!rawProduct.is_86d,
    image:
      rawProduct.image ||
      rawProduct.image_url ||
      rawProduct.photo_thumbnail ||
      null,
    active: rawProduct.active !== undefined ? rawProduct.active : true,
    sorting: rawProduct.sorting || 0
  };
}

/**
 * Transforms an array of products from API response to application models
 * @param {Array} rawProducts - Raw products array from API
 * @returns {Array} Transformed products array, sorted by sorting field
 */
export function transformProducts(rawProducts) {
  if (!Array.isArray(rawProducts)) {
    return [];
  }

  return rawProducts
    .map((product) => transformProduct(product))
    .filter((product) => product !== null)
    .sort((a, b) => (a.sorting || 0) - (b.sorting || 0));
}
